import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  PropsWithChildren,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Inbox,
  LoaderCircle,
  RefreshCw,
  X,
} from "lucide-react";
import {
  MARKETPLACE_CODES,
  MARKETPLACE_META,
  type ListingStatus,
  type MarketplaceCode,
} from "../types";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

export function Button({
  variant = "primary",
  loading = false,
  icon,
  children,
  className = "",
  disabled,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  loading?: boolean;
  icon?: ReactNode;
}) {
  return (
    <button
      {...props}
      disabled={disabled || loading}
      className={`button button--${variant} ${className}`}
    >
      {loading ? <LoaderCircle className="spin" size={16} aria-hidden /> : icon}
      <span>{children}</span>
    </button>
  );
}

export function IconButton({
  label,
  children,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button {...props} className={`icon-button ${className}`} aria-label={label} title={label}>
      {children}
    </button>
  );
}

export function Panel({
  children,
  className = "",
}: PropsWithChildren<{ className?: string }>) {
  return <section className={`panel ${className}`}>{children}</section>;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {description && <p className="page-description">{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  className = "",
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`field ${className}`}>
      <span className="field__label">{label}</span>
      {children}
      {hint && !error && <span className="field__hint">{hint}</span>}
      {error && <span className="field__error">{error}</span>}
    </label>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`input ${props.className ?? ""}`} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`input select ${props.className ?? ""}`} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`input textarea ${props.className ?? ""}`} />;
}

export function MarketplaceSelector({
  value,
  onChange,
  multiple = false,
  disabledCodes = [],
  label = "",
  ariaLabel = "Amazon-Marktplatz auswählen",
  compact = false,
}: {
  value: MarketplaceCode | MarketplaceCode[];
  onChange: (value: MarketplaceCode | MarketplaceCode[]) => void;
  multiple?: boolean;
  disabledCodes?: MarketplaceCode[];
  label?: string;
  ariaLabel?: string;
  compact?: boolean;
}) {
  const selected = Array.isArray(value) ? value : [value];
  const toggle = (code: MarketplaceCode) => {
    if (disabledCodes.includes(code)) return;
    if (!multiple) {
      onChange(code);
      return;
    }
    const next = selected.includes(code)
      ? selected.filter((entry) => entry !== code)
      : [...selected, code];
    onChange(MARKETPLACE_CODES.filter((entry) => next.includes(entry)));
  };

  return (
    <div className={`marketplace-selector ${compact ? "marketplace-selector--compact" : ""}`}>
      {label && <span className="marketplace-selector__label">{label}</span>}
      <div className="marketplace-selector__items" role="group" aria-label={label || ariaLabel}>
        {MARKETPLACE_CODES.map((code) => {
          const meta = MARKETPLACE_META[code];
          const active = selected.includes(code);
          const disabled = disabledCodes.includes(code);
          return (
            <button
              type="button"
              key={code}
              className={`marketplace-pill ${active ? "is-active" : ""}`}
              onClick={() => toggle(code)}
              disabled={disabled}
              aria-pressed={active}
              aria-label={`${meta.label} (${code})${disabled ? " – nicht verfügbar" : ""}`}
              title={meta.label}
            >
              <span className="marketplace-pill__code" aria-hidden>{code}</span>
              <span className="marketplace-pill__name" aria-hidden>{meta.label}</span>
              {multiple && active && <Check size={12} aria-hidden />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function MarketplaceMark({
  code,
  className = "",
  decorative = false,
}: {
  code: MarketplaceCode;
  className?: string;
  decorative?: boolean;
}) {
  const meta = MARKETPLACE_META[code];
  return (
    <span
      className={`marketplace-mark ${className}`}
      aria-hidden={decorative || undefined}
      title={meta.label}
    >
      <span aria-hidden>{code}</span>
      {!decorative && <span className="sr-only">{meta.label} ({code})</span>}
    </span>
  );
}

const listingLabels: Record<ListingStatus, string> = {
  active: "Aktiv",
  missing: "Fehlt",
  inactive: "Inaktiv",
  suppressed: "Unterdrückt",
  restricted: "Gesperrt",
  pending: "In Prüfung",
  unknown: "Unbekannt",
};

const statusLabels: Record<string, string> = {
  ...listingLabels,
  connected: "Verbunden",
  degraded: "Eingeschränkt",
  disconnected: "Getrennt",
  success: "Erfolgreich",
  warning: "Warnung",
  error: "Fehler",
  neutral: "Neutral",
  queued: "Eingereiht",
  running: "Läuft",
  retrying: "Erneuter Versuch",
  succeeded: "Erfolgreich",
  failed: "Fehlgeschlagen",
  cancelled: "Abgebrochen",
  prepared: "Vorbereitet",
  reserved: "Reserviert",
  submitted: "Übermittelt",
  accepted: "Akzeptiert",
  live: "Live",
  rejected: "Abgelehnt",
  superseded: "Ersetzt",
};

export function StatusPill({
  status,
  label,
  dot = true,
}: {
  status:
    | ListingStatus
    | "connected"
    | "degraded"
    | "disconnected"
    | "success"
    | "warning"
    | "error"
    | "neutral"
    | "queued"
    | "running"
    | "retrying"
    | "succeeded"
    | "failed"
    | "cancelled"
    | "prepared"
    | "reserved"
    | "submitted"
    | "accepted"
    | "live"
    | "rejected"
    | "superseded";
  label?: string;
  dot?: boolean;
}) {
  const resolvedLabel = label ?? statusLabels[status] ?? status;
  return (
    <span className={`status-pill status-pill--${status}`}>
      {dot && <span className="status-pill__dot" aria-hidden />}
      {resolvedLabel}
    </span>
  );
}

export function StatePanel({
  type,
  title,
  description,
  action,
  compact = false,
}: {
  type: "loading" | "empty" | "error";
  title?: string;
  description?: string;
  action?: ReactNode;
  compact?: boolean;
}) {
  const defaults = {
    loading: {
      title: "Daten werden geladen",
      description: "Einen Moment – die aktuellen Amazon-Daten werden abgerufen.",
      icon: <LoaderCircle className="spin" size={22} />,
    },
    empty: {
      title: "Noch keine Daten",
      description: "Für diese Auswahl wurden keine Einträge gefunden.",
      icon: <Inbox size={20} />,
    },
    error: {
      title: "Das hat nicht funktioniert",
      description: "Bitte versuche es erneut.",
      icon: <AlertTriangle size={22} />,
    },
  }[type];
  return (
    <div className={`state-panel state-panel--${type} ${compact ? "state-panel--compact" : ""}`}>
      <div className="state-panel__icon">{defaults.icon}</div>
      <div>
        <strong>{title ?? defaults.title}</strong>
        <p>{description ?? defaults.description}</p>
        {action && <div className="state-panel__action">{action}</div>}
      </div>
    </div>
  );
}

export function InlineError({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <StatePanel
      type="error"
      compact
      description={message}
      action={
        onRetry ? (
          <Button variant="secondary" onClick={onRetry} icon={<RefreshCw size={15} />}>
            Erneut laden
          </Button>
        ) : undefined
      }
    />
  );
}

export function Metric({
  label,
  value,
  detail,
  tone = "default",
}: {
  label: string;
  value: string | number;
  detail?: string;
  tone?: "default" | "positive" | "warning" | "danger" | "cyan";
}) {
  return (
    <div className={`metric metric--${tone}`}>
      <span className="metric__label">{label}</span>
      <strong className="metric__value">{value}</strong>
      {detail && <span className="metric__detail">{detail}</span>}
    </div>
  );
}

export function Drawer({
  open,
  title,
  subtitle,
  onClose,
  children,
}: PropsWithChildren<{
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
}>) {
  if (!open) return null;
  return (
    <div className="drawer-layer" role="presentation">
      <button className="drawer-backdrop" onClick={onClose} aria-label="Detailansicht schließen" />
      <aside className="drawer" role="dialog" aria-modal="true" aria-labelledby="drawer-title">
        <header className="drawer__header">
          <div>
            <p className="eyebrow">Listing-Detail</p>
            <h2 id="drawer-title">{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <IconButton label="Schließen" onClick={onClose}>
            <X size={20} />
          </IconButton>
        </header>
        <div className="drawer__body">{children}</div>
      </aside>
    </div>
  );
}

export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  size = "medium",
}: PropsWithChildren<{
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  footer?: ReactNode;
  size?: "small" | "medium" | "large";
}>) {
  if (!open) return null;
  return (
    <div className="modal-layer">
      <button className="modal-backdrop" onClick={onClose} aria-label="Dialog schließen" />
      <section
        className={`modal modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <header className="modal__header">
          <div>
            <h2 id="modal-title">{title}</h2>
            {description && <p>{description}</p>}
          </div>
          <IconButton label="Schließen" onClick={onClose}>
            <X size={20} />
          </IconButton>
        </header>
        <div className="modal__body">{children}</div>
        {footer && <footer className="modal__footer">{footer}</footer>}
      </section>
    </div>
  );
}

export function RowAction({ children }: PropsWithChildren) {
  return (
    <span className="row-action">
      {children}
      <ChevronRight size={16} aria-hidden />
    </span>
  );
}

export function Skeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="skeleton-list" aria-label="Inhalt wird geladen">
      {Array.from({ length: lines }, (_, index) => (
        <span key={index} className="skeleton" style={{ width: `${92 - index * 8}%` }} />
      ))}
    </div>
  );
}

export function formatMoney(value?: number, currency = "EUR") {
  if (typeof value !== "number" || Number.isNaN(value)) return "–";
  return new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(value);
}

export function formatDate(value?: string) {
  if (!value) return "–";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}
